import type { HTMLAttributes } from "react";
import { forwardRef, createContext, useCallback, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@renewmap/ui/utils/class-name";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = () => (
  <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none">
    <X className="h-6 w-6" />
    <span className="sr-only">Close</span>
  </DialogPrimitive.Close>
);

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const dialogContentClasses =
  "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 sm:rounded-lg";

const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /**
     * Element to return focus to when the dialog closes. Defaults to whatever was
     * focused when the dialog opened. Pass this for dialogs opened from a dropdown
     * menu item, where the opener is unmounted before it can be captured.
     */
    restoreFocusRef?: React.RefObject<HTMLElement | null>;
  }
>(
  (
    { className, children, onCloseAutoFocus, restoreFocusRef, ...props },
    ref,
  ) => {
    // These dialogs are controlled (no DialogTrigger), so Radix's built-in close focus
    // return targets a null trigger ref and drops focus to <body>. Capture the element
    // focused when the content mounts — in the ref callback, which runs before the focus
    // trap / aria-hidden effects blur it — and restore it (or restoreFocusRef) on close.
    const openerRef = useRef<HTMLElement | null>(null);
    const setContentRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (node) {
          openerRef.current = document.activeElement as HTMLElement | null;
        }
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogContext.Provider value={true}>
          <DialogPrimitive.Content
            ref={setContentRef}
            className={cn(dialogContentClasses, className)}
            onCloseAutoFocus={(event) => {
              onCloseAutoFocus?.(event);
              const target = restoreFocusRef?.current ?? openerRef.current;
              if (!event.defaultPrevented && target?.isConnected) {
                event.preventDefault();
                target.focus();
              }
            }}
            {...props}
          >
            {children}
            <DialogClose />
          </DialogPrimitive.Content>
        </DialogContext.Provider>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};

// not autogenerated
export const DialogContext = createContext(false);
